import React, { createContext, useContext, useEffect, useState } from 'react'
import { User } from '../types/entities'
import { apiClient, setTokenProvider, setCsrfToken } from '../services/ApiClient'

interface AuthResponse {
  token: string | null // Nullable - now sent via HttpOnly cookie
  refreshToken: string | null // Nullable - now sent via HttpOnly cookie
  csrfToken: string | null // CSRF token for client to include in headers
  expiresAt: string
  user: User
  requiresMfa?: boolean // For MFA login flow
  requiresMfaSetup?: boolean // For admin users who must enable MFA before logging out
}

interface LoginRequest {
  email: string
  password: string
}

interface MfaLoginRequest {
  email: string
  code: string
}

interface RegisterRequest {
  email: string
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
  phoneNumber?: string
  timeZone?: string
  companyName?: string
  marketingConsent?: boolean
  organizationSlug?: string
}

interface AuthContextType {
  isAuthenticated: boolean
  isLoading: boolean
  user: User | null
  requiresMfaSetup: boolean
  login: (credentials: LoginRequest) => Promise<{ success: boolean; message: string; requiresMfa?: boolean }>
  loginWithMfa: (credentials: MfaLoginRequest) => Promise<{ success: boolean; message: string }>
  register: (userData: RegisterRequest) => Promise<{ success: boolean; message: string }>
  logout: () => Promise<void>
  getAccessToken: () => string | null
}

const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: React.ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [requiresMfaSetup, setRequiresMfaSetup] = useState(false)

  // Setup token provider for ApiClient (cookie-based auth, no bearer token)
  useEffect(() => {
    setTokenProvider(async () => null)
  }, [])

  // Initialize auth state from cookies
  useEffect(() => {
    const initializeAuth = async () => {
      console.log('[AuthContext] Initializing auth, current path:', window.location.pathname)

      // Skip auth validation on public pages
      const publicPaths = [
        '/', // Landing page - don't check auth at all
        '/login',
        '/signup',
        '/register',
        '/forgot-password',
        '/reset-password',
        '/terms',
        '/privacy',
        '/faq',
        '/accept-invitation',
        '/auth/accept-invitation',
        '/verify-email',
        '/auth/verify-email',
        '/welcome/activate', // New verification URL
        '/marketplace'
      ]
      const isPublicPage = publicPaths.some(path => window.location.pathname.includes(path))

      if (isPublicPage) {
        console.log('[AuthContext] Public page detected, skipping auth check')
        setIsLoading(false)
        return
      }

      try {
        // Try to verify authentication by fetching current user
        // If cookies are valid, this will succeed
        console.log('[AuthContext] Checking authentication via /auth/me')
        const userData = await apiClient.get<User>('/auth/me')
        console.log('[AuthContext] User authenticated:', userData.email, 'Role:', userData.role, 'MFA:', userData.mfaEnabled)
        setUser(userData)
        setIsAuthenticated(true)

        // Check if admin user needs to set up MFA
        // This is required for security - admins cannot use the system without MFA
        if (userData.role === 'Admin' && !userData.mfaEnabled) {
          console.log('[AuthContext] Admin without MFA detected - showing setup modal')
          setRequiresMfaSetup(true)
        }
      } catch (error: any) {
        console.log('[AuthContext] Authentication check failed:', error.message || error)
        clearAuthData()

        // Redirect to login on 401 (only if not already on a public page)
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          const currentPath = window.location.pathname
          const publicRoutes = [
            '/', // Landing page
            '/login',
            '/signup',
            '/register',
            '/forgot-password',
            '/reset-password',
            '/terms',
            '/privacy',
            '/faq'
          ]
          const isPublicRoute = publicRoutes.some(route => currentPath === route || currentPath.includes(route))

          if (!isPublicRoute) {
            window.location.href = `/login?returnUrl=${encodeURIComponent(currentPath)}`
          }
        }
      }

      setIsLoading(false)
    }

    initializeAuth()
  }, [])

  // Note: Token expiration is now handled by HttpOnly cookies
  // The backend manages cookie expiration and rotation
  // Periodically check authentication status by pinging /auth/me
  useEffect(() => {
    const checkAuthStatus = async () => {
      if (!isAuthenticated) return

      try {
        await apiClient.get<User>('/auth/me')
      } catch (error: any) {
        // If 401, session expired - clear auth and redirect
        if (error.message?.includes('401') || error.message?.includes('Unauthorized')) {
          console.log('Session expired, logging out')
          clearAuthData()
          window.location.href = '/login'
        }
      }
    }

    // Check every 5 minutes
    const intervalId = setInterval(checkAuthStatus, 5 * 60 * 1000)

    return () => clearInterval(intervalId)
  }, [isAuthenticated])

  const clearAuthData = () => {
    try {
      localStorage.removeItem('auth_token')
      localStorage.removeItem('refresh_token')
    } catch (e) {
      // localStorage blocked by browser privacy settings
      console.warn('localStorage access blocked:', e)
    }
    // Clear CSRF token from memory
    setCsrfToken(null)
    setUser(null)
    setIsAuthenticated(false)
  }

  const login = async (credentials: LoginRequest): Promise<{ success: boolean; message: string; requiresMfa?: boolean }> => {
    try {
      setIsLoading(true)

      // Backend expects PascalCase property names
      const loginData = {
        Email: credentials.email,
        Password: credentials.password
      }

      const authData = await apiClient.post<AuthResponse>('/auth/login', loginData)

      // Check if MFA is required
      if (authData.requiresMfa) {
        return {
          success: false,
          message: 'MFA verification required',
          requiresMfa: true
        }
      }

      // Store CSRF token in memory (tokens are now in HttpOnly cookies)
      if (authData.csrfToken) {
        setCsrfToken(authData.csrfToken)
      }

      // Set user state
      setUser(authData.user)
      setIsAuthenticated(true)

      return { success: true, message: 'Login successful' }
    } catch (error: any) {
      console.error('Login error:', error.message || error)
      const message = error.message || 'Login failed. Please try again.'
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const loginWithMfa = async (credentials: MfaLoginRequest): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true)

      // Backend expects PascalCase property names
      const loginData = {
        Email: credentials.email,
        Code: credentials.code
      }

      const authData = await apiClient.post<AuthResponse>('/auth/login/mfa', loginData)

      // Store CSRF token in memory (tokens are now in HttpOnly cookies)
      if (authData.csrfToken) {
        setCsrfToken(authData.csrfToken)
      }

      // Set user state
      setUser(authData.user)
      setIsAuthenticated(true)

      return { success: true, message: 'Login successful' }
    } catch (error: any) {
      console.error('MFA login error:', error.message || error)
      const message = error.message || 'MFA verification failed. Please try again.'
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const register = async (userData: RegisterRequest): Promise<{ success: boolean; message: string }> => {
    try {
      setIsLoading(true)

      // Ensure TimeZone is set (use browser timezone or default to UTC)
      // Note: Backend expects PascalCase property names
      const registrationData = {
        Email: userData.email,
        FirstName: userData.firstName,
        LastName: userData.lastName,
        Password: userData.password,
        ConfirmPassword: userData.confirmPassword,
        PhoneNumber: userData.phoneNumber,
        TimeZone: userData.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
        CompanyName: userData.companyName,
        MarketingConsent: userData.marketingConsent || false,
        OrganizationSlug: userData.organizationSlug
      }

      await apiClient.post<User>('/auth/register', registrationData)

      return {
        success: true,
        message: 'Registration successful! Please check your email to verify your account.'
      }
    } catch (error: any) {
      console.error('Registration error:', error.message || error)
      const message = error.message || 'Registration failed. Please try again.'
      return { success: false, message }
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    try {
      // Call logout endpoint to invalidate token on server (if implemented)
      await apiClient.post<void>('/auth/logout', {})
    } catch (error: any) {
      console.error('Logout error:', error.message || error)
    } finally {
      clearAuthData()
    }
  }

  const getAccessToken = (): string | null => {
    return null
  }

  const value: AuthContextType = {
    isAuthenticated,
    isLoading,
    user,
    requiresMfaSetup,
    login,
    loginWithMfa,
    register,
    logout,
    getAccessToken,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
